(function () {
  'use strict';

  const TRUSTOO_REVIEW_WRAPPER_SELECTOR = '#trustoo-widget-wrapper';
  const INFORMATION_TABS_SECTION_SELECTOR = '[id$="__information-tabs"]';
  const RELOCATED_TABS_SHELL_CLASS = 'bk-scent-relocated-tabs-shell';
  const RELOCATED_DESCRIPTION_SELECTOR = [
    '.bls__template-product-2 .bls__products-tabs .product.info.detailed',
    '.bls__template-product-2 .bls__product-tabs-content .product.info.detailed',
  ].join(', ');

  function clampScentOffset(value, maxOffset) {
    return Math.max(-maxOffset, Math.min(maxOffset, value));
  }

  function calculateScentOffset(pointerX, pointerY, centerX, centerY, strength, maxDistance, maxOffset) {
    const dx = pointerX - centerX;
    const dy = pointerY - centerY;
    const distance = Math.hypot(dx, dy);
    if (distance === 0 || distance >= maxDistance) return { x: 0, y: 0 };
    const influence = (1 - distance / maxDistance) * (strength / 100);
    const magnitude = maxOffset * influence;
    return {
      x: clampScentOffset(-(dx / distance) * magnitude, maxOffset),
      y: clampScentOffset(-(dy / distance) * magnitude, maxOffset),
    };
  }

  function getScentScrollBehavior(prefersReducedMotion) {
    return prefersReducedMotion ? 'auto' : 'smooth';
  }

  function getScentShopifySection(scentElement) {
    if (!scentElement) return null;
    const section = scentElement.closest?.('.shopify-section');
    if (section) return section;
    const parent = scentElement.parentElement;
    return parent?.id?.startsWith('shopify-section-') ? parent : null;
  }

  function placeTrustooReviewsAfterScent(scentElement, root) {
    const scentSection = getScentShopifySection(scentElement);
    const mainContent = root?.querySelector?.('#MainContent');
    const reviewWrapper = mainContent?.querySelector?.(TRUSTOO_REVIEW_WRAPPER_SELECTOR);
    if (!scentSection || !reviewWrapper || scentSection === reviewWrapper || scentSection.contains(reviewWrapper)) {
      return false;
    }

    if (scentSection.parentNode !== mainContent || reviewWrapper.parentNode !== mainContent) return false;
    if (scentSection.nextElementSibling === reviewWrapper) return true;

    mainContent.insertBefore(reviewWrapper, scentSection.nextSibling);
    return scentSection.nextElementSibling === reviewWrapper;
  }

  function collapseRelocatedInformationTabsShell(scentElement, root) {
    const scentSection = getScentShopifySection(scentElement);
    const mainContent = root?.querySelector?.('#MainContent');
    const informationTabsSection = mainContent?.querySelector?.(INFORMATION_TABS_SECTION_SELECTOR);
    const tabsShell = informationTabsSection?.querySelector?.('.bls__product-tabs');
    if (
      !scentSection
      || !informationTabsSection
      || !tabsShell
      || scentSection.parentNode !== mainContent
      || informationTabsSection.parentNode !== mainContent
    ) {
      return false;
    }

    if (tabsShell.querySelector?.('.product.info.detailed')) return false;
    if (!mainContent.querySelector?.(RELOCATED_DESCRIPTION_SELECTOR)) return false;

    tabsShell.classList.add(RELOCATED_TABS_SHELL_CLASS);
    return tabsShell.classList.contains(RELOCATED_TABS_SHELL_CLASS);
  }

  function observeTrustooReviewsAfterScent(scentElement, options = {}) {
    const root = options.root || (typeof document !== 'undefined' ? document : null);
    const scentSection = getScentShopifySection(scentElement);
    const mainContent = root?.querySelector?.('#MainContent');
    const MutationObserverClass = options.MutationObserverClass
      || (typeof MutationObserver !== 'undefined' ? MutationObserver : null);
    const setTimer = options.setTimer || (typeof setTimeout === 'function' ? setTimeout : null);
    const clearTimer = options.clearTimer || (typeof clearTimeout === 'function' ? clearTimeout : null);
    const noop = function () {};

    if (!mainContent || scentSection?.parentNode !== mainContent) return noop;
    let reviewsPlaced = placeTrustooReviewsAfterScent(scentElement, root);
    let tabsShellCollapsed = collapseRelocatedInformationTabsShell(scentElement, root);
    if (reviewsPlaced && tabsShellCollapsed) return noop;
    if (!MutationObserverClass || !setTimer || !clearTimer) return noop;

    let observer;
    let timeoutId;
    let stopped = false;
    const stop = function () {
      if (stopped) return;
      stopped = true;
      observer?.disconnect();
      if (timeoutId !== undefined) clearTimer(timeoutId);
    };
    const reconcileAndStop = function () {
      if (!reviewsPlaced) reviewsPlaced = placeTrustooReviewsAfterScent(scentElement, root);
      if (!tabsShellCollapsed) {
        tabsShellCollapsed = collapseRelocatedInformationTabsShell(scentElement, root);
      }
      if (reviewsPlaced && tabsShellCollapsed) stop();
    };

    observer = new MutationObserverClass(reconcileAndStop);
    observer.observe(mainContent, { childList: true, subtree: true });
    timeoutId = setTimer(stop, options.timeoutMs || 15_000);
    reconcileAndStop();
    return stop;
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      clampScentOffset,
      calculateScentOffset,
      getScentScrollBehavior,
      collapseRelocatedInformationTabsShell,
      placeTrustooReviewsAfterScent,
      observeTrustooReviewsAfterScent,
    };
  }

  if (typeof window === 'undefined' || typeof customElements === 'undefined') return;
  if (customElements.get('bk-scent-ritual')) return;

  class BKScentRitual extends HTMLElement {
    connectedCallback() {
      this.reviewPlacementCleanup?.();
      this.reviewPlacementCleanup = observeTrustooReviewsAfterScent(this);
      this.objects = Array.from(this.querySelectorAll('[data-scent-object]'));
      this.productObject = this.querySelector('[data-scent-product]');
      this.annotation = this.querySelector('[data-scent-annotation]');
      this.motionQuery = window.matchMedia('(pointer: fine) and (prefers-reduced-motion: no-preference)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.pointer = null;
      this.frame = null;
      this.geometry = [];
      this.onPointerEnter = this.refreshGeometry.bind(this);
      this.onPointerMove = this.queuePointer.bind(this);
      this.onPointerLeave = this.resetMotion.bind(this);
      this.onPointerOver = this.handleFocus.bind(this);
      this.onFocusIn = this.handleFocus.bind(this);
      this.onClick = this.handleFocus.bind(this);
      this.onMotionChange = () => this.resetMotion();
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointermove', this.onPointerMove);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('click', this.onClick);
      if (this.motionQuery.addEventListener) {
        this.motionQuery.addEventListener('change', this.onMotionChange);
      } else {
        this.motionQuery.addListener?.(this.onMotionChange);
      }
    }

    disconnectedCallback() {
      this.reviewPlacementCleanup?.();
      this.reviewPlacementCleanup = null;
      this.removeEventListener('pointerenter', this.onPointerEnter);
      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('pointerleave', this.onPointerLeave);
      this.removeEventListener('pointerover', this.onPointerOver);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('click', this.onClick);
      if (this.motionQuery?.removeEventListener) {
        this.motionQuery.removeEventListener('change', this.onMotionChange);
      } else {
        this.motionQuery?.removeListener?.(this.onMotionChange);
      }
      if (this.frame !== null) cancelAnimationFrame(this.frame);
    }

    refreshGeometry() {
      this.geometry = this.objects.map((element) => {
        const rect = element.getBoundingClientRect();
        return {
          element,
          centerX: rect.left + rect.width / 2,
          centerY: rect.top + rect.height / 2,
          strength: Number(element.dataset.motionStrength || 60),
          maxOffset: element.matches('[data-scent-product]') ? 8 : 22,
        };
      });
    }

    queuePointer(event) {
      if (!this.motionQuery.matches) return;
      this.pointer = { x: event.clientX, y: event.clientY };
      if (this.frame !== null) return;
      this.frame = requestAnimationFrame(() => this.applyMotion());
    }

    applyMotion() {
      this.frame = null;
      if (!this.pointer || !this.motionQuery.matches) return;
      for (const item of this.geometry) {
        const offset = calculateScentOffset(
          this.pointer.x,
          this.pointer.y,
          item.centerX,
          item.centerY,
          item.strength,
          180,
          item.maxOffset
        );
        item.element.style.setProperty('--bk-scent-x', `${offset.x.toFixed(2)}px`);
        item.element.style.setProperty('--bk-scent-y', `${offset.y.toFixed(2)}px`);
      }
    }

    resetMotion() {
      this.pointer = null;
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      this.frame = null;
      for (const element of this.objects || []) {
        element.style.removeProperty('--bk-scent-x');
        element.style.removeProperty('--bk-scent-y');
      }
    }

    handleFocus(event) {
      const optionsLink = event.target.closest?.('[data-scent-choose-options]');
      if (optionsLink && event.type === 'click') {
        const mainForm = document.querySelector(
          '.bls__template-main-product product-form form, .bls__template-main-product [data-type="add-to-cart-form"]'
        );
        if (mainForm) {
          event.preventDefault();
          mainForm.scrollIntoView({
            behavior: getScentScrollBehavior(this.reducedMotionQuery.matches),
            block: 'center',
          });
          const firstControl = mainForm.querySelector(
            'select:not([disabled]), input:not([type="hidden"]):not([disabled]), button:not([disabled])'
          );
          firstControl?.focus({ preventScroll: true });
        }
        return;
      }

      const note = event.target.closest?.('[data-scent-note]');
      if (!note || !this.contains(note)) return;
      const description = note.dataset.noteDescription || note.textContent.trim();
      if (this.annotation && description) this.annotation.textContent = description;
      for (const button of this.querySelectorAll('[data-scent-note]')) {
        const isActive = button === note;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      }
    }
  }

  customElements.define('bk-scent-ritual', BKScentRitual);
})();
